import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PlusIcon, XMarkIcon, ClockIcon, CalendarIcon } from '@heroicons/react/24/solid';
import { useConfirm } from './ConfirmDialog';

const localizer = momentLocalizer(moment);

const Schedule = ({ classroomId, userId, userRole, socket }) => {
  const confirm = useConfirm();
  const [sessions, setSessions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPostponeModal, setShowPostponeModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = userRole === 'admin';
  const isTutor = userRole === 'tutor';
  const isStudent = userRole === 'student' || (!isAdmin && !isTutor);

  // Fetch sessions
  useEffect(() => {
    fetchSessions();
  }, [classroomId]);

  // Listen for real-time session updates
  useEffect(() => {
    if (!socket) return;

    socket.on('session-created', (session) => {
      setSessions(prev => [...prev, session]);
      toast.success('New session scheduled!');
    });

    socket.on('session-updated', (session) => {
      setSessions(prev => prev.map(s => s.id === session.id ? session : s));
      toast.success('Session updated');
    });

    socket.on('session-deleted', ({ sessionId }) => {
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      toast.success('Session deleted');
    });

    return () => {
      socket.off('session-created');
      socket.off('session-updated');
      socket.off('session-deleted');
    };
  }, [socket]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/sessions/${classroomId}`);
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  // Transform sessions for react-big-calendar
  const events = sessions.map(session => ({
    id: session.id,
    title: session.title,
    start: new Date(session.start_time),
    end: new Date(session.end_time),
    resource: session
  }));

  const handleSelectSlot = ({ start, end }) => {
    if (!isAdmin) {
      toast.error('Only admins can create new sessions');
      return;
    }
    setSelectedSession({
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      title: '',
      description: ''
    });
    setShowCreateModal(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedSession(event.resource);
    setShowDetailModal(true);
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/sessions', {
        classroom_id: classroomId,
        created_by: userId,
        ...selectedSession
      });

      setShowCreateModal(false);
      setSelectedSession(null);
      toast.success('Session created successfully!');
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create session');
    }
  };

  const handlePostponeSession = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/sessions/${selectedSession.id}`, {
        start_time: selectedSession.start_time,
        end_time: selectedSession.end_time,
        title: selectedSession.title,
        description: selectedSession.description
      });

      setShowPostponeModal(false);
      setShowDetailModal(false);
      setSelectedSession(null);
      toast.success('Session rescheduled successfully!');
      fetchSessions();
    } catch (error) {
      console.error('Error postponing session:', error);
      toast.error('Failed to reschedule session');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!(await confirm({
      title: 'Delete session?',
      message: 'This removes the scheduled session for everyone. This can\'t be undone.',
      confirmLabel: 'Delete',
    }))) return;

    try {
      await axios.delete(`/api/sessions/${sessionId}`);
      setShowDetailModal(false);
      setSelectedSession(null);
      toast.success('Session deleted successfully!');
      fetchSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Failed to delete session');
    }
  };

  return (
    <div className="h-full w-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 py-2 px-4 flex items-center justify-between bg-gray-50">
        <div className="flex items-center space-x-2">
          <CalendarIcon className="h-5 w-5 text-primary-600" />
          <h2 className="text-base font-semibold text-gray-800">Class Schedule</h2>
        </div>
        {isAdmin && (
          <button
            onClick={() => {
              setSelectedSession({
                start_time: new Date().toISOString(),
                end_time: new Date(Date.now() + 3600000).toISOString(),
                title: '',
                description: ''
              });
              setShowCreateModal(true);
            }}
            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Schedule Session</span>
          </button>
        )}
      </div>

      {/* Calendar */}
      <div className="flex-1 p-2" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading schedule...</p>
            </div>
          </div>
        ) : (
          <div style={{ height: '100%', minHeight: '400px' }}>
            <style>{`
              .rbc-calendar {
                overflow: hidden;
              }
              .rbc-time-view,
              .rbc-month-view,
              .rbc-agenda-view {
                padding-bottom: 0 !important;
                margin-bottom: 0 !important;
              }
              .rbc-time-content {
                overflow-y: auto !important;
                max-height: calc(100vh - 280px) !important;
                padding-bottom: 0 !important;
              }
              .rbc-time-content::-webkit-scrollbar {
                width: 8px;
              }
              .rbc-time-content::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 4px;
              }
              .rbc-time-content::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 4px;
              }
              .rbc-time-content::-webkit-scrollbar-thumb:hover {
                background: #555;
              }
              .rbc-time-slot {
                min-height: 40px !important;
              }
              .rbc-day-slot {
                padding-bottom: 0 !important;
              }
              .rbc-time-gutter,
              .rbc-time-header-gutter {
                width: 85px !important;
                min-width: 85px !important;
                max-width: 85px !important;
              }
              .rbc-time-header-content {
                border-left: none !important;
              }
              .rbc-allday-cell {
                height: 20px !important;
                min-height: 20px !important;
              }
              .rbc-row.rbc-allday-cell {
                display: none !important;
              }
            `}</style>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable={isAdmin}
              views={['month', 'week', 'day', 'agenda']}
              defaultView="week"
              popup
            />
          </div>
        )}
      </div>

      {/* Create/Edit Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Schedule New Session</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setSelectedSession(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateSession} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session Title *
                </label>
                <input
                  type="text"
                  required
                  value={selectedSession?.title || ''}
                  onChange={(e) => setSelectedSession({ ...selectedSession, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Math Tutoring Session"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={selectedSession?.description || ''}
                  onChange={(e) => setSelectedSession({ ...selectedSession, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  rows="3"
                  placeholder="Add session details..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={moment(selectedSession?.start_time).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => setSelectedSession({ ...selectedSession, start_time: new Date(e.target.value).toISOString() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={moment(selectedSession?.end_time).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => setSelectedSession({ ...selectedSession, end_time: new Date(e.target.value).toISOString() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedSession(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Create Session
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Session Detail Modal */}
      {showDetailModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{selectedSession.title}</h3>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedSession(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-3">
              {selectedSession.description && (
                <div>
                  <p className="text-sm text-gray-600">{selectedSession.description}</p>
                </div>
              )}

              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <ClockIcon className="h-5 w-5" />
                <span>
                  {moment(selectedSession.start_time).format('MMMM D, YYYY [at] h:mm A')}
                </span>
              </div>

              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <ClockIcon className="h-5 w-5" />
                <span>
                  to {moment(selectedSession.end_time).format('h:mm A')}
                </span>
              </div>

              <div className="text-sm text-gray-500">
                Duration: {moment.duration(moment(selectedSession.end_time).diff(moment(selectedSession.start_time))).asMinutes()} minutes
              </div>
            </div>

            <div className="flex space-x-3 pt-6">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedSession(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>

              {/* Tutor can only postpone */}
              {isTutor && (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setShowPostponeModal(true);
                  }}
                  className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  Postpone
                </button>
              )}

              {/* Admin can edit and delete */}
              {isAdmin && (
                <>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowPostponeModal(true);
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteSession(selectedSession.id)}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Postpone/Edit Session Modal */}
      {showPostponeModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {isAdmin ? 'Edit Session' : 'Postpone Session'}
              </h3>
              <button
                onClick={() => {
                  setShowPostponeModal(false);
                  setSelectedSession(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handlePostponeSession} className="space-y-4">
              {isAdmin && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Session Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={selectedSession?.title || ''}
                      onChange={(e) => setSelectedSession({ ...selectedSession, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Math Tutoring Session"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={selectedSession?.description || ''}
                      onChange={(e) => setSelectedSession({ ...selectedSession, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      rows="3"
                      placeholder="Add session details..."
                    />
                  </div>
                </>
              )}

              {isTutor && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> As a tutor, you can only reschedule the session time. Contact an admin if you need to make other changes.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={moment(selectedSession?.start_time).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => setSelectedSession({ ...selectedSession, start_time: new Date(e.target.value).toISOString() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={moment(selectedSession?.end_time).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => setSelectedSession({ ...selectedSession, end_time: new Date(e.target.value).toISOString() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPostponeModal(false);
                    setSelectedSession(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors ${
                    isAdmin
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  }`}
                >
                  {isAdmin ? 'Save Changes' : 'Postpone'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schedule;
